import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { Button, createBemBlockBuilder, Loading } from '@ebsco-ui/ebsco-ui';

import {
  FeatureFlagDisplay,
  getErrorReason,
  Modal,
  ModalNotificationType,
  RecordSummaryModalHeader,
  RenewNotification,
} from '@app/components';
import { useMappingsContext, useRecordSummaryContext } from '@app/contexts';
import {
  queryKeys,
  useAppLocale,
  useHoldsAndCheckoutsPageConfig,
} from '@app/hooks';
import { getCheckoutsStatusMessage, getFormattedDate } from '@app/utils';
import {
  itemStatusPanelButtonMessages,
  sharedMessages,
} from '@app/translations';
import { Feature, NotificationStatus } from '@app/constants';

import { InactiveAccountNotification } from '../InactiveAccountNotification';
import { useRenewMutation } from './useRenewMutation';

import '../Modal.scss';

interface RenewModalProps {
  isOpen: boolean;
  onModalClose: () => void;
}

const cnBem = createBemBlockBuilder(['modal']);

export const RenewModal: FC<RenewModalProps> = ({ isOpen, onModalClose }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ModalNotificationType | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const intl = useIntl();
  const { $t } = intl;
  const { locale } = useAppLocale();
  const renewMutation = useRenewMutation();
  const { instance, itemStatusData } = useRecordSummaryContext();
  const { selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption } =
    useHoldsAndCheckoutsPageConfig();
  const { userItemStatuses } = useMappingsContext();

  const isUserAccountActive = userItemStatuses?.isActive !== false;
  const modalTitle = selectConfiguredHoldsAndCheckoutsOption(
    $t({
      id: 'renewModal.renewLoan',
      defaultMessage: 'Renew loan',
    }),
    $t({
      id: 'renewModal.renewCheckout',
      defaultMessage: 'Renew checkout',
    })
  );

  useEffect(() => {
    const queryStatus = renewMutation.status;

    if (
      queryStatus === NotificationStatus.error ||
      queryStatus === NotificationStatus.success
    ) {
      setStatus(queryStatus);
    }

    if (queryStatus === NotificationStatus.success) {
      const date = getFormattedDate({
        date: renewMutation.data.dueDate,
        locale,
      });

      setStatusMessage(getCheckoutsStatusMessage({ date, intl }));
    }
  }, [instance.id, locale, renewMutation.data, renewMutation.status, intl]);

  const handleCloseButtonClick = () => {
    if (status === NotificationStatus.success) {
      queryClient.refetchQueries([queryKeys.holdsAndCheckoutsPage], {
        type: 'active',
      });
      queryClient.refetchQueries([queryKeys.userItemStatuses], {
        type: 'active',
      });
    }

    onModalClose();
  };

  const getModalFooter = () => {
    return (
      <>
        <Button
          data-auto="modal-footer-close"
          styleType="text"
          onClick={handleCloseButtonClick}
        >
          {$t(status ? sharedMessages.close : sharedMessages.cancel)}
        </Button>
        {!status && (
          <Button
            title={$t(itemStatusPanelButtonMessages.renew)}
            styleType="default"
            disabled={!isUserAccountActive}
            onClick={() =>
              renewMutation.mutate({
                itemId: itemStatusData?.itemId as string,
              })
            }
          >
            {$t(itemStatusPanelButtonMessages.renew)}
          </Button>
        )}
      </>
    );
  };

  return (
    <FeatureFlagDisplay
      flagToMatch={{ name: Feature.renewHold, isActiveIfNotFound: true }}
    >
      <Modal
        isOpen={isOpen}
        modalTitle={modalTitle}
        className={cnBem()}
        onClose={onModalClose}
        onAfterClose={() => setStatus(null)}
      >
        <div
          className={cn({
            [cnBem('__disabledScroll')]: renewMutation.isLoading,
          })}
        >
          <RecordSummaryModalHeader
            headerTitle={modalTitle}
            onModalClose={onModalClose}
          />
          {!isUserAccountActive && (
            <Modal.Body>
              <InactiveAccountNotification />
            </Modal.Body>
          )}
          {status && (
            <Modal.Body>
              <RenewNotification
                type={status}
                statusMessage={statusMessage}
                errorMessage={getErrorReason(renewMutation, $t)}
              />
            </Modal.Body>
          )}
          <Modal.Footer>{getModalFooter()}</Modal.Footer>
          {renewMutation.isLoading && (
            <Loading
              loadingMessage={selectConfiguredHoldsAndCheckoutsOption(
                $t({
                  id: 'renewModal.renewingLoan',
                  defaultMessage: 'Renewing loan...',
                }),
                $t({
                  id: 'renewModal.renewingCheckout',
                  defaultMessage: 'Renewing checkout...',
                })
              )}
            />
          )}
        </div>
      </Modal>
    </FeatureFlagDisplay>
  );
};
